<script setup>
/**
 * Este componente es un checkbox que al hacer click llama a una URL del servidor
 * para que cambie el estado de lo que sea. Si el servidor devuelve un código correcto
 * mantiene el cambio, si no, lo revierte. Mientras está llamando muestra un spinner
 */
import { nextTick, ref, watch } from 'vue';
import axios from 'axios';
import toast from '@/libs/cifra/toast';
import Spinner from '../Spinner.vue';
import InputCheckboxBoolean from '../cifra/InputCheckboxBoolean.vue';

const props = defineProps({
  // El valor del checkbox. Solo true o false para marcado o no
  modelValue: { type: Boolean, required: true },
  url: { type: String, required: true },
  method: { type: String, default: 'post' },
  params: { type: Object, default: () => ({}) },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const checked = ref(props.modelValue);
const loading = ref(false);
const lastValue = ref(props.modelValue);

watch(() => props.modelValue, (val) => {
  checked.value = val;
  lastValue.value = val;
});

watch(checked, async (newValue) => {
  if (loading.value || props.disabled) return;

  loading.value = true;
  try {
    const response = await axios({
      url: props.url,
      method: props.method,
      data: { ...props.params, value: newValue },
    });

    if (response.status >= 200 && response.status < 300) {
      lastValue.value = newValue;
      emit('update:modelValue', newValue);
    } else {
      throw new Error('Respuesta no exitosa');
    }
  } catch (e) {
    // Si falla, revertimos el valor al último conocido
    checked.value = lastValue.value;
    toast.apiErrors(e);
  } finally {
    // Lo lanzamos en el siguiente tick para darle tiempo a que si ha fallado
    // restaure el valor del checkbox sin que vuelva a llamar al servidor
    nextTick(() => {
      loading.value = false;
    });
  }
});
</script>

<template>
  <label style="display: inline-flex; align-items: center; cursor: pointer;">
    <InputCheckboxBoolean
      v-show="!loading"
      v-model="checked"
      :disabled="loading || disabled"
      class="m-n"
    />
    <Spinner v-if="loading" style="font-size: 0.4em; margin-left: 0.5em;" />
  </label>
</template>
